/**
 * Value formatting for AppGrid, accepting the format values screens passed
 * to DevExtreme columns, so a migrated column kept its `format` prop:
 *
 *   dates    'MM/dd/yyyy', 'M/d/yyyy', 'shortDate', 'yyyy-MM-dd', 'shortTime', 'h:mm a'
 *   numbers  '$#,##0', '$#,##0.00', '#,##0', '#0.0', "#0.0'%'", '#,##0.00'
 *   objects  { type: 'fixedPoint' | 'currency' | 'percent' | 'decimal', precision }
 *   names    'currency', 'fixedPoint', 'percent', 'decimal'
 *   function (value) => string
 *
 * Output is always US formatting (en-US), as the web ports require.
 */

export type GridDataType = 'string' | 'number' | 'date' | 'datetime' | 'boolean';

export type GridFormatObject = {
  type: 'fixedPoint' | 'currency' | 'percent' | 'decimal';
  precision?: number;
};

export type GridFormat = string | GridFormatObject | ((value: unknown) => string);

/* ------------------------------------------------------------------ */
/*  Dates                                                              */
/* ------------------------------------------------------------------ */

/** Date, ISO string or epoch ms to a Date. A bare yyyy-MM-dd is a LOCAL date
 *  (new Date('2026-10-20') would be UTC midnight and show the 19th in ET). */
export function toDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

const DATE_ALIASES: Record<string, string> = {
  shortDate: 'M/d/yyyy',
  shortTime: 'h:mm a',
  shortDateShortTime: 'M/d/yyyy, h:mm a',
  longDate: 'EEEE, MMMM d, yyyy',
  monthAndYear: 'MMMM yyyy',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** LDML subset: yyyy yy MMMM MMM MM M dd d EEEE HH H hh h mm ss a, quoted literals. */
export function formatDatePattern(d: Date, pattern: string): string {
  const p = DATE_ALIASES[pattern] ?? pattern;
  let out = '';
  let i = 0;
  while (i < p.length) {
    const c = p[i];
    if (c === "'") {
      const end = p.indexOf("'", i + 1);
      out += p.slice(i + 1, end < 0 ? p.length : end);
      i = end < 0 ? p.length : end + 1;
      continue;
    }
    let run = 1;
    while (p[i + run] === c) run++;
    const h12 = d.getHours() % 12 || 12;
    switch (c) {
      case 'y': out += run === 2 ? pad(d.getFullYear() % 100) : String(d.getFullYear()); break;
      case 'M': out += run >= 4 ? MONTHS[d.getMonth()] : run === 3 ? MONTHS[d.getMonth()].slice(0, 3) : run === 2 ? pad(d.getMonth() + 1) : String(d.getMonth() + 1); break;
      case 'd': out += run === 2 ? pad(d.getDate()) : String(d.getDate()); break;
      case 'E': out += run >= 4 ? DAYS[d.getDay()] : DAYS[d.getDay()].slice(0, 3); break;
      case 'H': out += run === 2 ? pad(d.getHours()) : String(d.getHours()); break;
      case 'h': out += run === 2 ? pad(h12) : String(h12); break;
      case 'm': out += pad(d.getMinutes()); break;
      case 's': out += pad(d.getSeconds()); break;
      case 'a': out += d.getHours() < 12 ? 'AM' : 'PM'; break;
      default: out += c.repeat(run);
    }
    i += run;
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Numbers                                                            */
/* ------------------------------------------------------------------ */

interface NumberPattern {
  prefix: string;
  suffix: string;
  grouping: boolean;
  minFrac: number;
  maxFrac: number;
  percent: boolean;
}

const patternCache = new Map<string, NumberPattern | null>();

/** '$#,##0.00' / "#0.0'%'" / '#,##0' into its parts; null if it is not a number pattern. */
export function parseNumberPattern(pattern: string): NumberPattern | null {
  const hit = patternCache.get(pattern);
  if (hit !== undefined) return hit;
  // Strip quoted literals into placeholders so their characters don't parse.
  const literals: string[] = [];
  const bare = pattern.replace(/'([^']*)'/g, (_m, lit: string) => {
    literals.push(lit);
    return `\uE000${literals.length - 1}\uE000`;
  });
  const m = /^(.*?)([#0,]+(?:\.[#0]+)?)(.*)$/.exec(bare);
  if (!m) {
    patternCache.set(pattern, null);
    return null;
  }
  const unquote = (s: string) => s.replace(/\uE000(\d+)\uE000/g, (_x, n: string) => literals[Number(n)]);
  const [, pre, body, post] = m;
  const [, frac = ''] = body.split('.');
  const parsed: NumberPattern = {
    prefix: unquote(pre),
    suffix: unquote(post),
    grouping: body.includes(','),
    minFrac: (frac.match(/0/g) ?? []).length,
    maxFrac: frac.length,
    percent: post.includes('%') && !/'[^']*%[^']*'/.test(pattern),
  };
  patternCache.set(pattern, parsed);
  return parsed;
}

const nfCache = new Map<string, Intl.NumberFormat>();
function nf(minFrac: number, maxFrac: number, grouping: boolean): Intl.NumberFormat {
  const key = `${minFrac}|${maxFrac}|${grouping}`;
  let f = nfCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat('en-US', { minimumFractionDigits: minFrac, maximumFractionDigits: maxFrac, useGrouping: grouping });
    nfCache.set(key, f);
  }
  return f;
}

export function formatNumber(n: number, format: GridFormat | undefined): string {
  if (format == null) return nf(0, 10, false).format(n);
  if (typeof format === 'function') return format(n);
  if (typeof format === 'object') {
    const p = format.precision;
    switch (format.type) {
      case 'currency': return `${n < 0 ? '-' : ''}$${nf(p ?? 2, p ?? 2, true).format(Math.abs(n))}`;
      case 'percent': return `${nf(p ?? 0, p ?? 0, true).format(n * 100)}%`;
      case 'decimal': return nf(0, p ?? 10, false).format(n);
      default: return nf(p ?? 0, p ?? 0, true).format(n);
    }
  }
  if (format === 'currency') return formatNumber(n, { type: 'currency' });
  if (format === 'percent') return formatNumber(n, { type: 'percent' });
  if (format === 'fixedPoint') return formatNumber(n, { type: 'fixedPoint' });
  if (format === 'decimal') return formatNumber(n, { type: 'decimal' });
  const p = parseNumberPattern(format);
  if (!p) return String(n);
  const v = p.percent ? n * 100 : n;
  const body = nf(p.minFrac, p.maxFrac, p.grouping).format(Math.abs(v));
  return `${v < 0 ? '-' : ''}${p.prefix}${body}${p.suffix}`;
}

/* ------------------------------------------------------------------ */
/*  Entry point                                                        */
/* ------------------------------------------------------------------ */

function looksLikeDateFormat(f: string): boolean {
  return f in DATE_ALIASES || /[yMdHhms]/.test(f.replace(/'[^']*'/g, ''));
}

/** The text a cell shows for `value`. Blank for null / undefined / ''. */
export function formatValue(value: unknown, format: GridFormat | undefined, dataType: GridDataType | undefined): string {
  if (value == null || value === '') return '';
  if (typeof format === 'function') return format(value);
  const isDate = dataType === 'date' || dataType === 'datetime' || value instanceof Date
    || (typeof format === 'string' && looksLikeDateFormat(format) && dataType !== 'number');
  if (isDate) {
    const d = toDate(value);
    if (!d) return String(value);
    const pattern = typeof format === 'string' ? format : dataType === 'datetime' ? 'M/d/yyyy, h:mm a' : 'M/d/yyyy';
    return formatDatePattern(d, pattern);
  }
  if (typeof value === 'number') return formatNumber(value, format);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (dataType === 'number' && typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value)) && format != null) {
    return formatNumber(Number(value), format);
  }
  return String(value);
}

/** Excel number format for a grid format, so exported cells stay numbers / dates. */
export function excelNumFmt(format: GridFormat | undefined, dataType: GridDataType | undefined): string | undefined {
  if (dataType === 'date') return typeof format === 'string' && /^M+\/d+\/y+$/i.test(format) ? format.replace(/M/g, 'm').replace(/y+/, 'yyyy') : 'm/d/yyyy';
  if (dataType === 'datetime') return 'm/d/yyyy h:mm AM/PM';
  if (format == null || typeof format === 'function') return undefined;
  if (typeof format === 'object') {
    const p = format.precision ?? (format.type === 'currency' ? 2 : 0);
    const dec = p > 0 ? `.${'0'.repeat(p)}` : '';
    if (format.type === 'currency') return `"$"#,##0${dec}`;
    if (format.type === 'percent') return `0${dec}%`;
    return `#,##0${dec}`;
  }
  if (format === 'currency') return '"$"#,##0.00';
  if (format === 'percent') return '0%';
  const p = parseNumberPattern(format);
  if (!p) return undefined;
  const intPart = p.grouping ? '#,##0' : '0';
  const dec = p.maxFrac > 0 ? `.${'0'.repeat(p.minFrac)}${'#'.repeat(p.maxFrac - p.minFrac)}` : '';
  const q = (s: string) => (s ? `"${s.replace(/"/g, '')}"` : '');
  if (p.percent) return `${q(p.prefix)}${intPart}${dec}%`;
  return `${q(p.prefix)}${intPart}${dec}${q(p.suffix)}`;
}
